// src/services/notify.js — web notifications + sound effects, one module.
//
// Four kinds of event, four distinct sounds, all synthesized in WebAudio (no
// asset files, same approach as the call ringback in calls.js):
//
//   message        a soft two-note "pop-ding"           (socket 'new-message')
//   vibes          a brighter double chirp              (socket 'social-notification')
//   missed call    a mellow descending two-tone         (socket 'call-history-sync')
//   incoming call  a gentle four-note ringtone, looped  (calls.js status 'incoming')
//
// Plus the OS-level Notification when the tab isn't focused — clicking one
// focuses the window and deep-links (open the chat / open the post / show the
// dock). Everything is fail-soft: a blocked AudioContext or denied permission
// must never break the message itself.
//
// AUTOPLAY: browsers keep an AudioContext suspended until a user gesture. We
// hook one one-time pointer/key listener to unlock it; socket events that fire
// before the first gesture simply play nothing (the OS notification still shows).

import { getSocket } from './socket';
import { isChatLocked } from './chatLock';
import { getUserProfile } from './users';
import { decryptPerDeviceText } from '../crypto/e2ee';
/* ✦ one rule for "does this pierce a mute", read by the phone, the browser and the
   server's own push gate — see services/mentions */
import { mentionPiercesMute, mentionPushBody } from './mentions';

/* ── audio engine ─────────────────────────────────────────────────────────── */
let ctx = null;
function ac() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) {
    ctx = new AC();
    // unlock on the first gesture anywhere in the app
    const unlock = () => { try { ctx.resume(); } catch {} };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }
  if (ctx.state === 'suspended') { try { ctx.resume(); } catch {} }
  return ctx.state === 'running' ? ctx : null;
}

/** One decaying note. type 'sine'|'triangle'; t is seconds from now. */
function note(c, freq, t, dur, peak = 0.16, type = 'sine') {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type; o.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, c.currentTime + t);
  g.gain.exponentialRampToValueAtTime(peak, c.currentTime + t + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + t + dur);
  o.connect(g); g.connect(c.destination);
  o.start(c.currentTime + t); o.stop(c.currentTime + t + dur + 0.05);
}

/* ── per-chat notification prefs — WEB ONLY, localStorage, never synced ────── */
const NP_KEY = (chatId) => `allo_np_${chatId}`;
export const DEFAULT_CHAT_NOTIFY = {
  muted: false,       // master: nothing from this chat, ever
  preview: true,      // OS notification shows the decrypted text vs just "New message"
  tone: 'pop',        // message sound
  callAlerts: true,   // ringtone + banner for incoming calls from this person
  ringtone: 'allo',   // which ringtone
};
export function chatNotifySettings(chatId) {
  try { return { ...DEFAULT_CHAT_NOTIFY, ...(JSON.parse(localStorage.getItem(NP_KEY(chatId)) || '{}')) }; }
  catch { return { ...DEFAULT_CHAT_NOTIFY }; }
}
export function saveChatNotifySettings(chatId, s) {
  try { localStorage.setItem(NP_KEY(chatId), JSON.stringify(s)); } catch {}
}
const chatIdFor = (a, b) => [a, b].sort().join('_');

/* ── the tone library — every sound is synthesized, previewable in the UI ─── */
export const MESSAGE_TONES = [
  { id: 'pop', name: 'Pop' },
  { id: 'ding', name: 'Ding' },
  { id: 'chime', name: 'Chime' },
  { id: 'drop', name: 'Drop' },
  { id: 'none', name: 'Silent' },
];
export const RINGTONES = [
  { id: 'allo', name: 'Allo' },
  { id: 'marimba', name: 'Marimba' },
  { id: 'retro', name: 'Retro' },
  { id: 'calm', name: 'Calm' },
];

export function playMessageTone(id = 'pop') {
  if (id === 'none') return;
  const c = ac(); if (!c) return;
  if (id === 'ding') {
    note(c, 1319, 0, 0.5, 0.15, 'sine');
    note(c, 2637, 0, 0.25, 0.04, 'sine');
  } else if (id === 'chime') {
    note(c, 988, 0, 0.16, 0.12, 'triangle');
    note(c, 1245, 0.11, 0.16, 0.12, 'triangle');
    note(c, 1568, 0.22, 0.34, 0.12, 'triangle');
  } else if (id === 'drop') {
    const o = c.createOscillator(); const g = c.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(1240, c.currentTime);
    o.frequency.exponentialRampToValueAtTime(560, c.currentTime + 0.22);
    g.gain.setValueAtTime(0.0001, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, c.currentTime + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.3);
    o.connect(g); g.connect(c.destination);
    o.start(); o.stop(c.currentTime + 0.35);
  } else { // pop
    note(c, 830, 0, 0.16, 0.14, 'sine');
    note(c, 1245, 0.09, 0.28, 0.16, 'sine');
  }
}

export function playMessageSound() { playMessageTone('pop'); }

export function playVibeSound() {
  const c = ac(); if (!c) return;
  note(c, 988, 0, 0.1, 0.12, 'triangle');
  note(c, 1319, 0.12, 0.1, 0.12, 'triangle');
  note(c, 1568, 0.24, 0.22, 0.1, 'triangle');
}

export function playMissedCallSound() {
  const c = ac(); if (!c) return;
  note(c, 660, 0, 0.22, 0.14, 'triangle');
  note(c, 494, 0.2, 0.34, 0.14, 'triangle');
}

/* incoming-call ringtones: one bar per style, looped until stopRingtone(). */
let ringTimer = null;
function ringBar(c, t0, style = 'allo') {
  if (style === 'marimba') {
    const seq = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
    seq.forEach((f, i) => note(c, f, t0 + i * 0.15, 0.26, 0.15, 'triangle'));
  } else if (style === 'retro') {
    // the classic "brrring": a dense 440+480 tremolo burst
    for (let i = 0; i < 10; i += 1) {
      note(c, 440, t0 + i * 0.05, 0.045, 0.09, 'sine');
      note(c, 480, t0 + i * 0.05, 0.045, 0.09, 'sine');
    }
  } else if (style === 'calm') {
    note(c, 523.25, t0, 0.6, 0.11, 'sine');
    note(c, 659.25, t0 + 0.45, 0.75, 0.11, 'sine');
  } else { // allo
    const seq = [659.25, 880, 987.77, 880]; // E5 A5 B5 A5
    seq.forEach((f, i) => {
      note(c, f, t0 + i * 0.17, 0.32, 0.15, 'sine');
      note(c, f * 2, t0 + i * 0.17, 0.18, 0.045, 'sine'); // faint octave sparkle
    });
  }
}
export function startRingtone(style = 'allo') {
  stopRingtone();
  const cycle = () => {
    const c = ac();
    if (c) { ringBar(c, 0, style); ringBar(c, style === 'retro' ? 0.75 : 0.9, style); }
    ringTimer = setTimeout(cycle, 2800);
  };
  cycle();
}
export function stopRingtone() {
  if (ringTimer) { clearTimeout(ringTimer); ringTimer = null; }
}
/** One bar, for the settings UI's tap-to-hear. */
export function previewRingtone(style) {
  const c = ac(); if (!c) return;
  ringBar(c, 0, style);
}

/* ── OS notifications ─────────────────────────────────────────────────────── */
export function requestNotifyPermission() {
  try {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  } catch {}
}

function showNote({ title, body, icon, tag, onClick }) {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return null;
    const n = new Notification(title, {
      body: body || '', icon: icon || undefined, tag: tag || undefined, silent: true, // we ARE the sound
    });
    n.onclick = () => { try { window.focus(); } catch {} try { onClick && onClick(); } catch {} n.close(); };
    return n;
  } catch { return null; }
}

/* ── context from App: which chats are on screen right now ────────────────── */
let context = { openChatIds: [], myUid: null };
export function setNotifyContext(next) { context = { ...context, ...next }; }
const chatVisible = (chatId) =>
  document.hasFocus() && (context.openChatIds || []).includes(chatId);

/* profile cache for sender names/avatars */
const profCache = new Map();
async function prof(uid) {
  if (!uid) return null;
  if (profCache.has(uid)) return profCache.get(uid);
  const p = await getUserProfile(uid).catch(() => null);
  profCache.set(uid, p);
  return p;
}

/* ── the wiring ───────────────────────────────────────────────────────────── */
let attachedSocket = null;
const seenMsgs = new Set();     // messageId dedupe (server relays to every socket)
const seenCalls = new Set();    // call-history record dedupe

export async function attachNotify(myUid, { chatsRef, onOpenChat } = {}) {
  context.myUid = myUid;
  // the socket comes up a beat after login — same wait as attachCalls
  let s = getSocket();
  for (let i = 0; !s && i < 40; i++) {
    await new Promise((r) => setTimeout(r, 250)); // eslint-disable-line no-await-in-loop
    s = getSocket();
  }
  if (!s || s === attachedSocket) return;
  attachedSocket = s;

  // permission: try immediately, and again on the first gesture (some browsers
  // only honor a request made inside one)
  requestNotifyPermission();
  window.addEventListener('pointerdown', requestNotifyPermission, { once: true });

  /* 1 ▸ MESSAGES — every type. The server relays the full 'new-message' payload
     to each of the recipient's sockets, ciphertext included, so we can decrypt
     the preview right here with this browser's device key. */
  s.on('new-message', async (data) => {
    try {
      const { fromUserId, chatId, messageId, systemType, callData } = data || {};
      if (!fromUserId || fromUserId === myUid) return;       // my own echo
      if (systemType && !messageId) return;                  // reactions / votes — not messages
      if (callData) return;                                  // call bubbles ring elsewhere
      if (messageId) {
        if (seenMsgs.has(messageId)) return;
        seenMsgs.add(messageId);
        if (seenMsgs.size > 400) seenMsgs.delete(seenMsgs.values().next().value);
      }
      if (chatVisible(chatId)) return;                       // they're looking right at it

      /* ══ 🤖 A MENTION PIERCES A MUTED GROUP, AND ONLY A MENTION ════════════
         🟥 THE WHOLE POINT OF MUTING A BUSY ROOM is that it is quiet until somebody
            means YOU — and `np.muted` was the right rule for every message except that
            one. The flag rides the per-recipient envelope the sender addressed to this
            browser, because the server cannot read the message to see the "@" and
            neither can this handler until it has decrypted it (which is below, and
            gated on the preview setting, so it cannot be the source of truth here).
         🟢 mentionPiercesMute is the same function the server's push gate applies, so
            a muted group cannot be silent on the phone and loud in the tab. */
      const np = chatNotifySettings(chatId);
      const _mentionedMe = data.mentionedYou === true || data.mentionedYou === 'true';
      if (!mentionPiercesMute({ mentioned: _mentionedMe, isMuted: !!np.muted })) return;
      playMessageTone(np.tone);

      if (document.hasFocus()) return;                       // in-app sound is enough
      /* 🔒 a chat locked on the phone: no name, no picture, no message — just that something came */
      if (isChatLocked(chatId)) {
        if (!np.muted || _mentionedMe) showNote({ title: 'Allo', body: '1 new message', tag: `allo-msg-${chatId}`, onClick: () => onOpenChat && onOpenChat(chatId) });
        return;
      }
      const p = await prof(fromUserId);
      const who = p?.fullName || p?.username || 'New message';
      // group title if we can see it in the chat list
      let suffix = '';
      try {
        const chat = (chatsRef?.current || []).find((c) => c.id === chatId);
        if (chat?.isGroup) suffix = chat.name ? ` · ${chat.name}` : ' · Group';
      } catch {}
      // decrypt the text preview when the payload is sealed to this browser
      let body = '';
      if (np.preview) {
        try {
          if (data.perDeviceKeys && data.senderDeviceId) {
            body = (await decryptPerDeviceText({
              perDeviceKeys: data.perDeviceKeys, senderDeviceId: data.senderDeviceId, senderId: fromUserId,
            })) || '';
          }
        } catch {}
        if (!body) {
          const raw = String(data.message || '');
          body = raw && !raw.includes('🔒') ? raw : 'New message';
        }
      } else body = 'New message';
      /* ══ 🤖 TWO BANNERS, NOT ONE REWRITTEN ════════════════════════════
         🟥 THE FIRST VERSION REPLACED THE MESSAGE'S BODY with "X mentioned you in Y",
            so being named cost you the message preview — one notification, either the
            message or the mention. The phones now send the two as separate pushes;
            this is the same rule in the tab.
         🟢 AND EACH KEEPS ITS OWN GATE: the message respects the mute, the mention
            pierces it. In a muted group only the second one appears, which is exactly
            what muting a busy room is for.
         🟥 SEPARATE TAGS, or the browser collapses the second onto the first and the
            whole point is lost. */
      const _mGroup = (() => {
        try {
          const c = (chatsRef?.current || []).find((x) => x.id === chatId);
          return c?.name || c?.groupName || '';
        } catch { return ''; }
      })();
      if (!np.muted) {
        showNote({
          title: `${who}${suffix}`,
          body: body.slice(0, 120),
          icon: p?.profileImage,
          tag: `allo-msg-${chatId}`,
          onClick: () => onOpenChat && onOpenChat(chatId),
        });
      }
      if (_mentionedMe) {
        showNote({
          title: _mGroup || who,
          /* 🟢 shown even when previews are off — this sentence is not the message */
          body: mentionPushBody(who, _mGroup),
          icon: p?.profileImage,
          tag: `allo-mention-${chatId}`,
          onClick: () => onOpenChat && onOpenChat(chatId),
        });
      }
    } catch {}
  });

  /* 2 ▸ VIBES — likes, comments, shares, friend requests. The server mirrors
     every push it sends to the phones as a live 'social-notification'. */
  s.on('social-notification', async (data) => {
    try {
      if (!data || data.toUserId !== myUid) { /* server already targets us, but be safe */ }
      playVibeSound();
      if (document.hasFocus()) return;
      showNote({
        title: data.title || 'Vibes',
        body: data.body || '',
        icon: data.image || undefined,
        tag: 'allo-vibes',
        onClick: () => {
          const postId = data?.payload?.postId || data?.postId;
          if (postId) window.dispatchEvent(new CustomEvent('allo:open-post', { detail: { postId } }));
          else window.dispatchEvent(new CustomEvent('allo:open-vibes'));
        },
      });
    } catch {}
  });

  /* 3 ▸ MISSED CALLS — the canonical end-of-call record. Only fresh records
     ring (the server can replay history on reconnect). */
  s.on('call-history-sync', async (rec) => {
    try {
      const id = String(rec?.callSessionId || rec?.callId || '');
      if (!id || seenCalls.has(id)) return;
      seenCalls.add(id);
      if (seenCalls.size > 200) seenCalls.delete(seenCalls.values().next().value);
      const outcome = String(rec?.outcome || '');
      const from = String(rec?.callInitiatedBy || '');
      if (outcome !== 'missed' || !from || from === myUid) return;
      const ts = Number(rec?.eventTime) || 0;
      if (ts && Date.now() - ts > 2 * 60 * 1000) return;     // stale replay, badge only
      const np = chatNotifySettings(chatIdFor(myUid, from));
      if (np.muted || !np.callAlerts) return;                // silenced, web-side
      playMissedCallSound();
      const p = await prof(from);
      const who = p?.fullName || p?.username || 'Someone';
      const kind = rec?.callType === 'video' ? 'video' : 'voice';
      showNote({
        title: 'Missed call',
        body: `${who} · ${kind} call`,
        icon: p?.profileImage,
        tag: `allo-missed-${id}`,
        onClick: () => window.dispatchEvent(new CustomEvent('allo:open-calls')),
      });
    } catch {}
  });
}

/* ── incoming 1:1 call — driven by the calls engine state from App.jsx ────── */
let incomingNote = null;
let incomingFor = null;
export async function notifyIncomingCall(call) {
  if (!call || call.status !== 'incoming') return;
  if (incomingFor === call.callId) return;                    // already ringing
  incomingFor = call.callId;
  // 🟢 this caller's own web preferences: muted or call-alerts-off = no ring here
  //    (the phone still rings — these settings never leave this browser)
  const np = context.myUid && call.peerUid
    ? chatNotifySettings(chatIdFor(context.myUid, call.peerUid))
    : DEFAULT_CHAT_NOTIFY;
  if (np.muted || !np.callAlerts) return;
  startRingtone(np.ringtone);
  const p = call.callerData?.name
    ? { fullName: call.callerData.name, profileImage: call.callerData.image }
    : await prof(call.peerUid);
  const who = p?.fullName || p?.username || 'Incoming call';
  if (!document.hasFocus()) {
    incomingNote = showNote({
      title: `Incoming ${call.callType === 'video' ? 'video' : 'voice'} call`,
      body: who,
      icon: p?.profileImage,
      tag: 'allo-incoming-call',
      onClick: () => {},                                      // focus is enough — the dock is everywhere
    });
  }
}
export function stopIncomingNotify() {
  incomingFor = null;
  stopRingtone();
  try { incomingNote?.close(); } catch {}
  incomingNote = null;
}
